/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
import NProgress from 'nprogress';
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation
} from 'react-router-dom';
import React, { Suspense, Fragment, lazy, useEffect, useMemo } from 'react';
// material
// import { makeStyles } from '@mui/material/styles';
import { Typography, Box } from '@mui/material';
import { ErrorBoundary } from 'react-error-boundary';
import { styled } from '@mui/material/styles';
import DashboardLayout from '../layouts/dashboard';
import HomeLayout from '../layouts/home';

// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import { trackVisit } from '../utils/visitTracker';

// components
import LoadingScreen from '../components/LoadingScreen';

import { PATH_PAGE, PATH_AUTH, PATH_DASHBOARD } from './paths';
import DashboardRoutes from './dashboard.routes';
import HomeRoutes from './home.routes';

const RootStyle = styled('div')(({ theme }) => ({
  height: '100%',
  display: 'flex',
  textAlign: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(8, 2)
}));

function VisitTracker() {
  const location = useLocation();

  useEffect(() => {
    trackVisit();
  }, [location]);

  return null;
}

function ErrorFallback({ error, resetErrorBoundary }) {
  // Customize the error fallback component
  return (
    <RootStyle>
      <Box
        component="img"
        alt="500"
        src="/static/illustrations/illustration_500.svg"
        sx={{ width: '100%', maxHeight: 240, my: { xs: 5, sm: 10 } }}
      />

      <h2>Something went wrong:</h2>
      <pre>{error.message}</pre>
      <button type="button" onClick={resetErrorBoundary}>
        Try Again
      </button>
    </RootStyle>
  );
}

export function renderRoutes(routes = []) {
  return (
    <Suspense
      fallback={
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh' // Ensure the loading screen takes up the entire viewport
          }}
        >
          <LoadingScreen />
        </div>
      }
    >
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        key={window.location.pathname}
      >
        <Routes>
          {routes.map((route, idx) => {
            const Component = route.component;
            const Guard = route.guard || React.Fragment;
            const Layout = route.layout || React.Fragment;

            return (
              <Route
                key={`routes-${idx}`}
                path={route.path === '*' ? '*' : `${route.path}/*`}
                element={
                  <Guard>
                    <Layout>
                      {route.routes ? (
                        renderRoutes(route.routes)
                      ) : Component ? (
                        <Component />
                      ) : (
                        <div>Component not available</div>
                      )}
                    </Layout>
                  </Guard>
                }
              />
            );
          })}

          {DashboardRoutes.routes.map((route, idx) => {
            const Component = route.component;
            const Guard = AuthGuard || React.Fragment;
            const Layout = DashboardLayout || React.Fragment;

            return (
              <Route
                key={`routes-${idx}`}
                path={route.path === '*' ? '*' : `${route.path}/*`}
                element={
                  <Guard>
                    <VisitTracker />
                    <Layout>
                      {route.routes ? (
                        renderRoutes(route.routes)
                      ) : Component ? (
                        <Component />
                      ) : (
                        <div>Component not available</div>
                      )}
                    </Layout>
                  </Guard>
                }
              />
            );
          })}

          {HomeRoutes.routes.map((route, idx) => {
            const Component = route.component;
            const Guard = AuthGuard || React.Fragment;
            const Layout = HomeLayout || React.Fragment;

            return (
              <Route
                key={`routes-${idx}`}
                path={route.path === '*' ? '*' : `${route.path}/*`}
                element={
                  <Guard>
                    <VisitTracker />
                    <Layout>
                      {route.routes ? (
                        renderRoutes(route.routes)
                      ) : Component ? (
                        <Component />
                      ) : (
                        <div>Component not available</div>
                      )}
                    </Layout>
                  </Guard>
                }
              />
            );
          })}
        </Routes>
      </ErrorBoundary>
    </Suspense>
  );
}

const routes = [
  // Others Routes
  {
    path: '/',
    component: () => <Navigate to="/auth/login" replace />
  },
  {
    guard: GuestGuard,
    path: PATH_AUTH.login,
    component: lazy(() => import('../views/authentication/Login'))
  },
  {
    path: PATH_AUTH.loginUnprotected,
    component: lazy(() => import('../views/authentication/Login'))
  },
  {
    path: PATH_AUTH.resetPassword,
    component: lazy(() => import('../views/authentication/ResetPassword'))
  },
  {
    path: PATH_AUTH.verify,
    component: lazy(() => import('../views/authentication/VerifyCode'))
  },
  {
    path: PATH_PAGE.mobileAppRedirect,
    component: lazy(() => import('../views/MobileAppRedirect'))
  },
  {
    path: PATH_PAGE.comingSoon,
    component: lazy(() => import('../views/ComingSoon'))
  },
  {
    path: PATH_PAGE.maintenance,
    component: lazy(() => import('../views/Maintenance'))
  },
  {
    path: PATH_AUTH.root,
    component: () => <Navigate to={PATH_AUTH.login} />
  },
  {
    path: '*',
    component: lazy(() => import('../views/Page404'))
  },
  {
    path: PATH_PAGE.page404,
    component: lazy(() => import('../views/Page404'))
  },
  {
    path: PATH_PAGE.page500,
    component: lazy(() => import('../views/Page500'))
  }
];

export default routes;

export function AppRoutes() {
  return renderRoutes(routes);
}

ErrorFallback.propTypes = {
  error: PropTypes.object,
  resetErrorBoundary: PropTypes.func
};
